/**
 * O que os cartões do CRM fazem quando o consultor clica — spec-010 do
 * `project-plataforma-por-chat`, [T014] / US-1.
 *
 * ## Por que a ação mora no embrulho, e não sobe pelo `respond`
 *
 * Decisão D8 do plan: "abrir o registro" é abrir um MODAL (`GlobalBus`) ou
 * empurrar uma ROTA. Nenhuma das duas é resposta ao modelo, e mandá-las pelo
 * canal `respond` obrigaria quem está acima do `chat-message` — que serve três
 * superfícies diferentes — a conhecer cada modal de cada módulo. O embrulho já
 * é do CRM; é ele quem sabe.
 *
 * Estas funções são chamadas pelo mapa `efeitos` da fábrica
 * (`ui-registry/cartao-do-chat.js`), com `this` na instância do embrulho — é
 * daí que saem `$router` e `$showSnack`.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { MODAL } from '@constants/Text'
import { GlobalBus } from '@services/bus'
import { abrirRotaDoCartao } from '@modules/aawz-intelligence-v2/ui-registry/acoes-compartilhadas'
import { ROTA_DO_ATENDIMENTO } from '@modules/atendimento/ia/chaves'
import { abrirConversaDeAtendimento as abrirConversaNaCaixa } from '@modules/atendimento/ia/acoes'
import { apiCrmOportunidadeFunilCompleto } from '@modules/crm/api/apiCrmOportunidadeFunilCompleto'
import { findOportunidadeById } from '@modules/crm/utils/oportunidadeFunil'
import { apiCrmLeads } from '@modules/crm/api/apiCrmLeads'
import { resolveLeadTarget } from '@modules/crm/utils/clienteTarget'

// A tela de atendimento é aba do CRM; a rota é a do parcial do atendimento,
// reexportada em vez de redigitada (rota escrita duas vezes sobrevive ao rename).
export { ROTA_DO_ATENDIMENTO }

/**
 * A rota do kanban de oportunidades **do CRM**.
 *
 * ★ NÃO é `opportunities`, e a confusão é fácil: `rotaDoDestino('oportunidades')`
 * (o catálogo da navegação) resolve para a rota `opportunities`, que é outra
 * tela — o módulo `modules/opportunities`, de ofertas e alocação, gateado por
 * `FEATURES.OPPORTUNITIES`. O funil de vendas que estes cartões mostram vive em
 * `crm.opportunities` (`modules/crm/routes.js`, `path: 'oportunidades'`,
 * `can_show: FEATURES.CRM`). Abrir a outra levaria o consultor a uma tela que
 * não tem nada do que ele acabou de ler — com a impressão de ter acertado.
 *
 * `registry.unit.js` compara este nome com o `routes.js` do módulo: rota
 * renomeada não quebra teste nenhum sozinha, só deixa um botão que não abre.
 */
export const ROTA_DO_KANBAN = 'crm.opportunities'

/**
 * O kanban de LEADS do CRM — pipeline SEPARADO e ANTERIOR ao de oportunidades
 * (lead converte em oportunidade). `registry.unit.js` compara este nome com o
 * `routes.js` do módulo, mesma guarda de arquivo que `ROTA_DO_KANBAN`.
 */
export const ROTA_DO_KANBAN_DE_LEADS = 'crm.leads.kanban'

/**
 * A aba de relacionamento do CRM — onde a atividade sugerida se conclui.
 *
 * O contrato descreve a ação como `abrir_cliente(secao='crm')`, que é a TOOL de
 * front; do cartão, o equivalente honesto é a rota da aba, porque o cartão lista
 * atividades de VÁRIOS clientes e não há um cliente para abrir.
 */
export const ROTA_DO_RELACIONAMENTO = 'crm.relationship'

/** A aba de e-mails do CRM. */
export const ROTA_DOS_EMAILS = 'crm.emails'

const ERRO_AO_NAVEGAR =
  'Não foi possível abrir a tela. Tente pelo menu do CRM.'

/**
 * Empurra uma rota do CRM. `this` é a instância do embrulho.
 *
 * A receita (router? → `empurrarRota` → aviso de aba barrada → snack com a
 * saída) é a de `ui-registry/acoes-compartilhadas`, a mesma dos outros cinco
 * parciais; aqui só entram a rota e a mensagem do CRM.
 */
function abrir(vm, rota) {
  return abrirRotaDoCartao(vm, { name: rota }, { mensagemDeErro: ERRO_AO_NAVEGAR })
}

export function abrirKanbanDeOportunidades() {
  return abrir(this, ROTA_DO_KANBAN)
}

/** O kanban de leads (cartão `crm/funil-leads`). */
export function abrirKanbanDeLeads() {
  return abrir(this, ROTA_DO_KANBAN_DE_LEADS)
}

export const ERRO_AO_ABRIR_LEAD =
  'Não foi possível carregar este lead. Abra pelo kanban de leads do CRM.'

/**
 * O lead de `id` dentro do payload de `apiCrmLeads.buscarLista`, normalizado pro
 * vocabulário que `abrirLeadNoModal`/`resolveLeadTarget` esperam.
 *
 * ★ [achado ao vivo, 16/09] `apiCrmLeads.buscarLista` chama a rota CRUD crua
 * (`/v3/api/aawz-crm/crm-lead`, singular) — NÃO o kanban. `value` é uma LISTA
 * PLANA de linhas de `crm_lead` (o modelo SQLAlchemy real), não o shape
 * aninhado `funis[].etapas[].leads[]` que `list_crm_leads_prioridade` usa no
 * backend (esse é outro endpoint, o do kanban). A PK da linha é `id`, não
 * `id_lead`; e `crm_lead` não tem coluna própria de nome/telefone/e-mail —
 * esses só existem no `cliente` aninhado, quando há `id_cliente_app`.
 */
function normalizarLeadCru(row) {
  if (!row) return null
  const cliente = row.cliente && typeof row.cliente === 'object' ? row.cliente : {}
  return {
    ...row,
    id_lead: row.id,
    nome: cliente.nome_cliente || row.nome || null,
    email: row.email || cliente.email || null,
    telefone: row.telefone || cliente.telefone || null,
  }
}

function findLeadById(value, idLead) {
  if (!idLead) return null
  const alvo = String(idLead)
  const linhas = Array.isArray(value) ? value : []
  const achado = linhas.find((lead) => lead && String(lead.id) === alvo)
  return normalizarLeadCru(achado)
}

// ['novo', 'em_progresso'] — os dois status "abertos" (`ACTIVE_LEAD_STATUSES`
// do backend). O kanban filtra por status default 'em_progresso' quando
// nenhum é pedido (`DEFAULT_STATUS_FILTER`) — sem forçar os dois aqui, um lead
// 'novo' (o mesmo universo que `list_crm_leads_prioridade` lista) some da
// busca e o clique falha em silêncio com "não foi possível carregar".
const STATUS_LEADS_ABERTOS = ['novo', 'em_progresso']

/**
 * O REGISTRO do lead, como o kanban o entregaria ao modal.
 *
 * Mesma razão de `buscarRegistroDaOportunidade`: o cartão só tem o
 * vocabulário do fio (`id_lead`, `nome`, `funil`, `etapa`, `dias_na_etapa`),
 * sem os campos que o modal de edição espera (`descricao`, `origem`,
 * `segmentacao_por_pl`, `data_registro`, ...) — abrir com o item do cartão
 * cru deixaria essas abas em branco.
 *
 * @param {Object} opcoes
 * @param {string}  opcoes.id            O `id_lead`.
 * @param {number=} opcoes.idClienteApp  Vínculo, quando existe — leads sem
 *                                       cliente cadastrado mandam `null`.
 * @returns {Promise<Object|null>} O registro, ou `null` quando a busca falhou
 *                                 ou o lead não está mais aberto em funil nenhum.
 */
export async function buscarRegistroDoLead({ id, idClienteApp } = {}) {
  if (!id) return null
  const filters = { status: STATUS_LEADS_ABERTOS }
  if (idClienteApp) filters.id_cliente_app = idClienteApp
  const r = await apiCrmLeads.buscarLista({ filters })
  return r && r.status === 200 ? findLeadById(r.value, id) : null
}

/**
 * Abre o registro do lead no modal do CRM (`crm-lead-steps.vue`, globalmente
 * montado em `global-modals.vue` — alcançável de qualquer rota, inclusive a
 * `/home-ia`).
 *
 * `resolveLeadTarget` decide o MODO (lead × cliente) a partir do `status` real
 * do registro — nunca `'lead'` fixo: um lead que converteu ENTRE a consulta da
 * fila e o clique (raro, mas possível) tem que abrir como cliente, e é essa
 * função (a mesma que `lead-list.vue`/o kanban usam) quem resolve isso, sem
 * uma segunda régua aqui divergindo da de lá.
 */
export function abrirLeadNoModal(dados) {
  const item = dados && dados.item
  if (!item || !item.id_lead) return
  const target = resolveLeadTarget(item)
  GlobalBus.$emit(MODAL.CRM_LEAD_EDIT, {
    item: { ...item, mode: target.mode, id_lead: target.id_lead },
  })
}

/** A aba de relacionamento (cartão das atividades sugeridas). */
export function abrirRelacionamento() {
  return abrir(this, ROTA_DO_RELACIONAMENTO)
}

/** A aba de e-mails (cartão dos e-mails). */
export function abrirEmails() {
  return abrir(this, ROTA_DOS_EMAILS)
}

/** A tela de atendimento, sem conversa selecionada. */
export function abrirAtendimento() {
  return abrir(this, ROTA_DO_ATENDIMENTO)
}

/**
 * A tela de atendimento JÁ com a conversa aberta — a MESMA ação do parcial do
 * atendimento (`@modules/atendimento/ia/acoes`), que leva o id pela query da
 * rota: a tela abre em NOVA aba, cujo Vuex nasce vazio, então despachar
 * `selectConversa` aqui só mexeria na aba atual.
 */
export function abrirConversaDeAtendimento(dados) {
  return abrirConversaNaCaixa(this, dados)
}

/** O aviso de quem buscou o registro e não achou — a saída (o kanban) vai junto. */
export const ERRO_AO_ABRIR_OPORTUNIDADE =
  'Não foi possível carregar esta oportunidade. Abra pelo kanban do CRM.'

/**
 * O REGISTRO da oportunidade, como o kanban o entregaria ao modal ([T029]).
 *
 * Ponto único da busca que todo cartão faz antes de pedir
 * `MODAL.CRM_OPPORTUNITIES`: o cartão só tem o vocabulário do fio (`id`,
 * `nome`, `valor`), e o modal não busca nada — emitir o item do cartão abriria
 * o formulário sem `id_funil`/`id_etapa`, e salvar moveria a oportunidade para
 * a etapa padrão. O cartão de alertas do atendimento (review #2041) emitia
 * `{item: {id}}` cru por não ter esta função para chamar.
 *
 * @param {Object} opcoes
 * @param {string|number} opcoes.id            O id da oportunidade.
 * @param {number=}       opcoes.idClienteApp  Encolhe o payload quando o
 *                                             vínculo existe; sem ele a busca é
 *                                             no funil inteiro, como a tela.
 * @returns {Promise<Object|null>} O registro, ou `null` quando a busca falhou
 *                                 ou o id não está em funil nenhum.
 */
export async function buscarRegistroDaOportunidade({ id, idClienteApp } = {}) {
  if (!id) return null
  const r = await apiCrmOportunidadeFunilCompleto.carregar({
    idClienteApp: idClienteApp || null,
  })
  return r && r.status === 200 ? findOportunidadeById(r.value, id) : null
}

/**
 * Abre o registro da oportunidade no modal do CRM.
 *
 * ★ **O `item` chega COMPLETO, e isso não é detalhe ([T029]).**
 * `crm-opportunities-modal.open()` faz `this.item = { ...item }` e não busca
 * nada (`crm-opportunities-modal.vue:119-143`): com `{item: {id}}` o modal
 * abria o formulário VAZIO. Quem busca o registro é o cartão
 * (`buscarRegistroDaOportunidade`) — ele tem o estado de carregamento por
 * item —, e o que chega aqui é o mesmo objeto que o kanban entregaria, com
 * `id_funil`/`id_etapa`.
 *
 * As duas flags são as do kanban (`kanban-card-opportunity.openEdit`), pela
 * mesma razão de paridade: é o MESMO modal, e um payload diferente o faria se
 * comportar diferente conforme a porta de entrada.
 */
export function abrirOportunidadeNoModal(dados) {
  const item = dados && dados.item
  // Sem registro não há o que abrir — e um modal em branco é pior que botão
  // nenhum. Quem avisa o consultor é o cartão, que sabe o que falhou.
  if (!item || !item.id) return
  GlobalBus.$emit(MODAL.CRM_OPPORTUNITIES, {
    item: item,
    dontReloadRegRelationshipModal: true,
    allowEditClient: true,
  })
}
